import { useCallback } from 'react';
import { defineMessages } from 'react-intl';

import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { LoadableLazy } from '@confluence/loadable';
import type { PanelProps } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

const DetailsPanelHeaderIcon = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-useShowDetailsPanel" */ './DetailsPanelHeaderIcon.tsx'
			)
		).DetailsPanelHeaderIcon,
});

const DetailsPanelBody = LoadableLazy<PanelProps>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-useShowDetailsPanel" */ './DetailsPanelBody.tsx'))
			.DetailsPanelBody,
});

export const i18n: {
	detailsPanelHeaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	detailsPanelHeaderLabel: {
		id: 'details-panel.panel.header.label',
		defaultMessage: 'Details',
		description:
			'The text is shown as the heading label in the details panel header on the right-hand side of a Confluence page, indicating the panel displays page metadata and details.',
	},
});

export const useShowDetailsPanel = (): {
	isDetailsPanelShown: boolean;
	showDetailsPanel: ({ highlightLabels }?: { highlightLabels?: boolean }) => void;
} => {
	const { showObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();

	const showDetailsPanel = useCallback(
		({ highlightLabels }: { highlightLabels?: boolean } = {}): void => {
			showObjectSidebar(
				{
					id: PanelName.DetailsPanel,
					headerComponentElements: {
						HeaderIcon: DetailsPanelHeaderIcon,
						headerLabel: i18n.detailsPanelHeaderLabel,
					},
					BodyComponent: DetailsPanelBody,
					props: {
						highlightLabels,
					},
				},
				'push',
			);
		},
		[showObjectSidebar],
	);

	return {
		isDetailsPanelShown: isObjectSidebarShown && panel?.id === PanelName.DetailsPanel,
		showDetailsPanel,
	};
};
